import { type UIViewInjectedProps, UISref, UIView } from '@uirouter/react';
import { Button, Tabs } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { runInAction } from 'mobx';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { Loader } from '@/components/loader';
import { PERMISSION_OBJ_LOT, PERMISSION_UPDATE } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { lotStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

import { Details } from './details';
import { Analyses } from './analyses';
import { Weighings } from './weighings';
import { Fumigations } from './fumigations';
import { Pledges } from './pledges';
import { Waybills } from './waybills';

const MAX_WIDTH = 1300;
const { TabPane } = Tabs;

const EditLink = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_UPDATE]])(({ lotId }: { lotId: number }) => (
  <UISref to="base-layout.lots.edit" params={{ lotId }}>
    <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
      {getTranslatedString('lot.edit')}
    </Button>
  </UISref>
));

export const LotView = observer(({ transition }: UIViewInjectedProps) => {
  const lotId = +transition.router.globals.params?.lotId;
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedLot, setFetchedLot] = useState<cyrian.api.lots.Lot | null>(null);
  const lotFromStore = lotStore.items.find(({ id }: any) => id === lotId);
  // Use locally fetched lot so that list load (e.g. limit=50 from parent Lots table) does not wipe the viewed lot from store and close the drawer
  const lot = lotFromStore ?? fetchedLot;
  const title = `Lot #${lot?.number ?? ''}`;

  const onClose = useCallback(() => {
    transition.router.stateService.go('base-layout.lots');
  }, [transition]);

  useEffect(() => {
    setFetchedLot(null);
    setIsLoading(true);
    const request = api.lots.get(lotId);

    request.source
      .then((response) => {
        const fullLotData = response.data;
        setFetchedLot(fullLotData);
        runInAction(() => {
          const lotInStore = lotStore.items.find(({ id }) => id === lotId);
          if (lotInStore) {
            Object.assign(lotInStore, fullLotData);
          } else {
            // If the user lands directly on this page, the store might be empty.
            lotStore.addItem(fullLotData);
          }
        });
      })
      .catch((error) => {
        showError(error);
        onClose(); // Close drawer on error
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => request.cancel();
  }, [lotId, onClose]);

  const width = window.innerWidth - 250 > MAX_WIDTH ? MAX_WIDTH : window.innerWidth - 250;

  if (isLoading || !lot) {
    return <Loader />;
  }

  return (
    <>
      <Drawer onClose={onClose} visible={true} title={title} width={width} extra={<EditLink lotId={lotId} />}>
        <Tabs defaultActiveKey="details">
          <TabPane tab={'Details'} key="details" forceRender={true}>
            <Details lot={lot} />
          </TabPane>
          <TabPane tab={'Analyses'} key="analyses" forceRender={true}>
            <Analyses lotId={lotId} lot={lot} />
          </TabPane>
          <TabPane tab={'Weighing'} key="weighing" forceRender={true}>
            <Weighings lotId={lotId} lot={lot} />
          </TabPane>
          <TabPane tab={'Fumigation'} key="fumigation" forceRender={true}>
            <Fumigations lotId={lotId} lot={lot} />
          </TabPane>
          <TabPane tab={'Pledges'} key="pledges" forceRender={true}>
            <Pledges lotId={lotId} lot={lot} />
          </TabPane>
          <TabPane tab={'Waybills'} key="waybills" forceRender={true}>
            <Waybills lotId={lotId} />
          </TabPane>
        </Tabs>
      </Drawer>
      <UIView />
    </>
  );
});
